import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  tooltipClasses,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ExpandMore } from "@mui/icons-material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { getSidebarList } from "./sidebarMenuList";
import VerticalLayoutMenu from "./index";
import { useSelector } from "react-redux";

const color = "#96a7c7";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.drawer.main,
    color: color,
    maxWidth: 300,
    fontSize: theme.typography.pxToRem(12),
    margin: 0,
    borderRadius: "unset",
  },
  [`& .${tooltipClasses.tooltipPlacementRight}`]: {
    marginLeft: "0 !important",
  },
}));

const MobileSubMenuList = ({
  subList,
  isDrawerOpen,
  isTooltipComponent,
  t,
}) => {
  const roles = useSelector((state) => state.Login.roles);
  const isMobile = useMediaQuery("(max-width:1024px)");
  const [open, setOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [active, setActive] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const handleClick = (subList) => {
    setOpen(!open);

    if (subList.url) {
      navigate(subList.url);
    } else {
      if (isMobile && subList.url !== pathname) {
        setIsMobileOpen(!isMobileOpen);
      }
    }
  };

  useEffect(() => {
    const recurtionFn = (MENU) => {
      MENU.forEach((element) => {
        if (element.child) {
          recurtionFn(element.children);
        } else {
          if (
            pathname
              .toLowerCase()
              .includes(
                (subList.url || subList.name).toLowerCase().replace(/ /g, "")
              )
          ) {
            setOpen(true);
            setActive(true);
          } else {
            setOpen(false);
            setActive(false);
          }
        }
      });
    };
    recurtionFn(getSidebarList(roles));
  }, [pathname]);

  const tooltipProps = (subList) =>
    subList?.children?.length
      ? {
          title: (
            <VerticalLayoutMenu
              menuItem={subList?.children || []}
              isDrawerOpen={isDrawerOpen}
              isTooltipComponent={true}
            />
          ),
        }
      : subList?.isParent
      ? {
          title: (
            <VerticalLayoutMenu
              menuItem={[{ ...subList, isParent: false }] || []}
              isDrawerOpen={isDrawerOpen}
              isTooltipComponent={true}
            />
          ),
        }
      : {};
  const tooltipOpenProps = isMobile ? { open: isMobileOpen } : {};

  return (
    <ListItem
      key={subList.name}
      disablePadding
      sx={{ display: "block", color: active ? "white" : color }}
    >
      <HtmlTooltip
        {...tooltipOpenProps}
        placement="right-start"
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, isMobileOpen ? 0 : -14],
              },
            },
          ],
        }}
        {...tooltipProps(subList)}
      >
        <ListItemButton
          onClick={() => handleClick(subList)}
          sx={{
            gap: "10px",
            justifyContent: "center",
            pl: !isTooltipComponent
              ? subList.parentName
                ? subList.level > 1
                  ? `${subList.level * 12 + 32}px`
                  : "32px"
                : 2
              : 2,
            "&:hover *": {
              color: "white",
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: active ? "white" : color,
              minWidth: "unset",
              "& svg": {
                fontSize: isDrawerOpen ? "18px" : "20px",
              },
            }}
          >
            {subList.icon}
          </ListItemIcon>
          <ListItemText
            primary={t(subList.name)}
            sx={{
              display: isTooltipComponent
                ? "block"
                : isDrawerOpen
                ? "block"
                : "none",
              color: active ? "white" : color,
              "& span": { fontSize: "14px" },
            }}
          />
          {!subList?.isParent &&
            subList?.children?.length &&
            (!open ? (
              <KeyboardArrowRightIcon
                style={{
                  color: active ? "white" : color,
                  minWidth: "unset",
                  fontSize: "18px",
                }}
              />
            ) : (
              <ExpandMore
                style={{
                  color: active ? "white" : color,
                  minWidth: "unset",
                  fontSize: "18px",
                }}
              />
            ))}
        </ListItemButton>
      </HtmlTooltip>
    </ListItem>
  );
};

export default withTranslation()(MobileSubMenuList);

MobileSubMenuList.propTypes = {
  subList: PropTypes.object,
  isDrawerOpen: PropTypes.bool,
  isTooltipComponent: PropTypes.bool,
  t: PropTypes.func,
};
